
import { supabase } from "@/integrations/supabase/client";
import { ERROR_CODES } from "@/lib/errorCodes";

interface UploadResult {
  success: boolean;
  filePath?: string;
  error?: typeof ERROR_CODES[keyof typeof ERROR_CODES];
}

/**
 * Validates a file before upload
 */
function validateFile(file: File): { valid: boolean; error?: typeof ERROR_CODES[keyof typeof ERROR_CODES] } {
  // Check file type
  const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: ERROR_CODES.FILE_TYPE_NOT_SUPPORTED 
    };
  }
  
  // Check file size (5MB limit)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    return { 
      valid: false, 
      error: ERROR_CODES.FILE_TOO_LARGE 
    };
  }
  
  return { valid: true };
}

/**
 * Uploads a brand guidelines file to Supabase storage
 */
export async function uploadBrandGuidelines(
  file: File, 
  businessId: string
): Promise<UploadResult> {
  try {
    // Get authenticated user
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !sessionData.session) {
      return {
        success: false,
        error: ERROR_CODES.AUTH_REQUIRED
      };
    }
    
    // Validate the file
    const validation = validateFile(file);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error
      };
    }
    
    // Create a unique file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${businessId}/guidelines-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;
    
    // Upload to Supabase storage
    const { data, error } = await supabase.storage
      .from('brand_guidelines')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });
    
    if (error) {
      console.error('Error uploading file:', error);
      return {
        success: false,
        error: ERROR_CODES.FILE_UPLOAD_FAILED
      };
    }
    
    return {
      success: true,
      filePath: data.path
    };
  } catch (error) {
    console.error('Exception during file upload:', error);
    return {
      success: false,
      error: ERROR_CODES.FILE_UPLOAD_FAILED
    };
  }
}

/**
 * Gets the public URL for a brand guidelines file
 */
export function getBrandGuidelinesUrl(filePath: string): string {
  const { data } = supabase.storage
    .from('brand_guidelines')
    .getPublicUrl(filePath);
  
  return data.publicUrl;
}
